// SortableTable.tsx
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination } from "@mui/material";
import React, { useEffect, useState } from "react";

interface TableProps<T extends Record<string, any>> {
  rows: T[];
  headCells: HeadCell<T>[];         // ora può includere 'actions'
  renderRow: (row: T) => JSX.Element;
  rowsPerPageOptions?: number[];
  initialRowsPerPage?: number;
}

export interface HeadCell<T> {
  id: keyof T | 'actions';           // 👈 permette colonna 'actions'
  label: string;
  numeric?: boolean;
  disablePadding?: boolean;
  disableSort?: boolean;
}

export type Order = 'asc' | 'desc';

function descendingComparator<T>(a: T, b: T, orderBy: keyof T | undefined) {
  if (!orderBy) return 0;
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key | undefined,
): (a: Record<Key, any>, b: Record<Key, any>) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function SortableTable<T extends Record<string, any>>({
  rows, headCells, renderRow,
  rowsPerPageOptions = [10, 25, 50],
  initialRowsPerPage = 25
}: TableProps<T>) {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof T | undefined>();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  const handleRequestSort = (property: keyof T | 'actions') => {
    if (property === 'actions') return;     // 👈 non ordinabile
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof T);
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedRows = orderBy ? stableSort(rows, getComparator(order, orderBy)) : rows;
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  useEffect(() => { setPage(0); }, [rows]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {headCells.map((headCell) => {
              const isActions = headCell.id === 'actions';
              return (
                <TableCell
                  key={String(headCell.id)}
                  align={headCell.numeric ? 'right' : 'left'}
                  padding={headCell.disablePadding ? 'none' : 'normal'}
                  sortDirection={!isActions && orderBy === headCell.id ? order : false}
                  style={{ minWidth: 120, textAlign: ['id_utente'].includes(headCell.id as string) ? 'left' : undefined }}
                >
                  {headCell.disableSort || isActions ? (
                    headCell.label
                  ) : (
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : 'asc'}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.isArray(sortedRows) && sortedRows.length > 0 ? (
            sortedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map(renderRow)
          ) : (
            <TableRow>
              <TableCell colSpan={headCells.length + 1} align="center">
                Nessun elemento da mostrare
              </TableCell>
            </TableRow>
          )}
          {emptyRows > 0 && (
            <TableRow style={{ height: 53 * emptyRows }}>
              <TableCell colSpan={headCells.length + 1} />
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
}

export default SortableTable;
